/* ============================================================
 * Feedback & Complaint (grievance) module API client.
 * Mirrors the expense api: a thin wrapper over the shared `api`
 * axios instance (auth token attached by interceptor).
 * Backend router prefix: /grievances
 * ========================================================== */

import api from "./api";

const GCS_BUCKET = "teamlio";
const GCS_PUBLIC_BASE = `https://storage.googleapis.com/${GCS_BUCKET}`;

/** Absolute, openable href for a stored attachment (same rules as expense
 *  receipts: absolute URLs pass through, a /files path is resolved against the
 *  API base, anything else is treated as a GCS object path). */
export const attachmentHref = (url?: string | null): string => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || /^blob:/i.test(raw) || /^data:/i.test(raw)) {
    return raw;
  }
  if (raw.startsWith("/files/")) {
    const base = process.env.REACT_APP_API_BASE_URL || "";
    return base ? `${base}${raw}` : raw;
  }
  return `${GCS_PUBLIC_BASE}/${raw
    .replace(/^\/+/, "")
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/")}`;
};

/** Open an attachment in a new tab. Fetched as a blob first so a file GCS
 *  serves with an awkward content-type still previews instead of downloading;
 *  falls back to the plain URL if that fetch is blocked. */
export const openAttachment = async (url?: string | null) => {
  const href = attachmentHref(url);
  if (!href) return;
  try {
    const res = await fetch(href);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const inlineUrl = URL.createObjectURL(await res.blob());
    const opened = window.open(inlineUrl, "_blank", "noopener,noreferrer");
    if (!opened) window.location.href = inlineUrl;
    setTimeout(() => URL.revokeObjectURL(inlineUrl), 60_000);
  } catch {
    const opened = window.open(href, "_blank", "noopener,noreferrer");
    if (!opened) window.location.href = href;
  }
};

/* ---------------- TYPES ---------------- */

export type GrievanceType =
  | "FEEDBACK"
  | "COMPLAINT"
  | "SUGGESTION"
  | "WORKPLACE_ISSUE";

export type GrievanceStatus =
  | "OPEN"
  | "IN_REVIEW"
  | "RESOLVED"
  | "CLOSED"
  | "REOPENED"
  | "WITHDRAWN";

export type GrievancePriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

/** "mine" = my own submissions. "assigned" = tickets assigned to me.
 *  "all" = the org-wide HR queue (needs the panel at Manage; without it the
 *  backend narrows this to mine + assigned rather than erroring). */
export type GrievanceScope = "mine" | "assigned" | "all";

export interface GrievanceAttachment {
  id?: string;
  message_id?: string | null;
  file_url: string;
  file_name: string;
  content_type?: string | null;
  size_bytes?: number | null;
  created_at?: string | null;
}

export interface GrievanceMessage {
  id: string;
  grievance_id: string;
  body: string | null;
  is_internal: boolean;
  author_user_id: string | null;
  author_name: string | null;
  /** What the author was acting AS on this ticket, not their job role. */
  author_role: "SUBMITTER" | "HANDLER" | null;
  created_at: string | null;
  attachments: GrievanceAttachment[];
}

export interface GrievanceEvent {
  id: string;
  action: string;
  from_value: string | null;
  to_value: string | null;
  note: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  is_internal: boolean;
  created_at: string | null;
}

export interface Grievance {
  id: string;
  ticket_code: string | null;
  type: GrievanceType;
  category: string | null;
  subject: string | null;
  description: string | null;
  priority: GrievancePriority;
  status: GrievanceStatus;
  is_anonymous: boolean;
  // Submitter — all null / "Anonymous" when the ticket is anonymous and the
  // viewer isn't the submitter. The backend redacts; never assume a name here.
  employeeID: string | null;
  name: string | null;
  department: string | null;
  designation: string | null;
  user_id: string | null;
  emptID: string | null;
  // Handling
  assigned_empt_id: string | null;
  assigned_user_id: string | null;
  assigned_name: string | null;
  assigned_on: string | null;
  // Outcome
  resolution: string | null;
  resolved_by_name: string | null;
  resolved_on: string | null;
  closed_on: string | null;
  reopen_count: number;
  withdrawn_on: string | null;
  submitted_on: string | null;
  last_activity_at: string | null;
  is_mine: boolean;
  unread_count: number;
  // Present on the detail payload only (null on list rows).
  attachments: GrievanceAttachment[] | null;
  messages: GrievanceMessage[] | null;
  events: GrievanceEvent[] | null;
  /** Detail payload only: may this viewer act as a handler on this ticket? */
  can_manage?: boolean;
}

export interface GrievanceStats {
  total: number;
  open: number;
  unassigned: number;
  urgent: number;
  resolved: number;
  closed: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
}

export interface GrievanceMeta {
  types: GrievanceType[];
  categories: Record<GrievanceType, string[]>;
  priorities: GrievancePriority[];
  statuses: GrievanceStatus[];
  /** Whether this user may work the HR queue. The page gates its Review tab on
   *  this rather than re-deriving the rule, so UI and API can't disagree. */
  can_manage: boolean;
  /** The caller's reporting manager — the submit form's "Send to" default.
   *  null when they have no manager on record, in which case the submission
   *  goes to the HR queue unassigned. */
  default_handler: GrievanceHandler | null;
  /** Whether the caller has direct reports. Drives the "Who my team can write
   *  to" control — a reporting manager curates that allow-list. */
  manages_team: boolean;
}

export interface GrievanceHandler {
  emptID: string;
  employeeID: string | null;
  name: string | null;
  designation: string | null;
  role: string | null;
}

/* ---------------- REFERENCE DATA ---------------- */

export const getGrievanceMeta = async (): Promise<GrievanceMeta> => {
  const res = await api.get("/grievances/meta");
  return res.data as GrievanceMeta;
};

/** Who the caller may address a submission to. A Manage grant gets the whole
 *  roster; everyone else gets their reporting manager plus whoever that
 *  manager has opened up. The HR team is always reachable on top of this — it
 *  is the picker's "no specific person" value, not a row here. */
export const getGrievanceHandlers = async (): Promise<GrievanceHandler[]> => {
  const res = await api.get("/grievances/handlers");
  return (res.data?.handlers || []) as GrievanceHandler[];
};

export interface TeamRecipients {
  /** Who this manager currently lets their team write to. */
  allowed: GrievanceHandler[];
  /** The org roster to pick more from. */
  roster: GrievanceHandler[];
}

export const getTeamRecipients = async (): Promise<TeamRecipients> => {
  const res = await api.get("/grievances/team-recipients");
  return {
    allowed: (res.data?.allowed || []) as GrievanceHandler[],
    roster: (res.data?.roster || []) as GrievanceHandler[],
  };
};

/** Replaces the whole allow-list — an id left out is a grant revoked. */
export const setTeamRecipients = async (allowedEmptIds: string[]) => {
  const res = await api.put("/grievances/team-recipients", {
    allowed_empt_ids: allowedEmptIds,
  });
  return res.data;
};

/* ---------------- LIST / STATS / DETAIL ---------------- */

export interface GrievanceFilters {
  status?: string;
  type?: string;
  priority?: string;
  unassigned?: boolean;
  search?: string;
}

export const getGrievances = async (
  scope: GrievanceScope = "mine",
  filters: GrievanceFilters = {}
): Promise<Grievance[]> => {
  const params = new URLSearchParams({ scope });
  if (filters.status) params.set("status", filters.status);
  if (filters.type) params.set("type", filters.type);
  if (filters.priority) params.set("priority", filters.priority);
  if (filters.unassigned) params.set("unassigned", "true");
  if (filters.search) params.set("search", filters.search);
  const res = await api.get(`/grievances?${params.toString()}`);
  return (res.data?.grievances || []) as Grievance[];
};

export const getGrievanceStats = async (
  scope: GrievanceScope = "mine"
): Promise<GrievanceStats> => {
  const res = await api.get(`/grievances/stats?scope=${scope}`);
  return res.data as GrievanceStats;
};

export const getGrievance = async (id: string): Promise<Grievance> => {
  const res = await api.get(`/grievances/${id}`);
  return res.data as Grievance;
};

/* ---------------- CREATE / EDIT ---------------- */

export interface GrievancePayload {
  type: GrievanceType;
  category?: string | null;
  subject: string;
  description: string;
  priority?: GrievancePriority;
  is_anonymous?: boolean;
  attachments?: GrievanceAttachment[];
  /** Who to address it to (an employee id). Omit to route to the submitter's
   *  reporting manager; send "" to leave it for the HR queue. */
  assigned_empt_id?: string;
}

export const createGrievance = async (payload: GrievancePayload) => {
  const res = await api.post("/grievances", payload);
  return res.data as Grievance;
};

/** Owner edit — accepted only while the ticket is still OPEN. */
export const updateGrievance = async (
  id: string,
  payload: Partial<Omit<GrievancePayload, "attachments" | "is_anonymous">>
) => {
  const res = await api.put(`/grievances/${id}`, payload);
  return res.data as Grievance;
};

/* ---------------- ATTACHMENT UPLOAD ---------------- */

export const uploadGrievanceAttachment = async (file: File) => {
  const form = new FormData();
  form.append("uploaded_file", file);
  const res = await api.post("/grievances/attachment", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data as GrievanceAttachment;
};

/* ---------------- HANDLER ACTIONS ---------------- */

/** IN_REVIEW | RESOLVED | CLOSED. `note` is REQUIRED for RESOLVED — the
 *  employee is told what was actually done. */
export const setGrievanceStatus = async (
  id: string,
  status: GrievanceStatus,
  note?: string
) => {
  const res = await api.put(
    `/grievances/${id}/status?status=${encodeURIComponent(status)}`,
    { note: note || null }
  );
  return res.data as Grievance;
};

/** Pass an empty/null emptID to unassign. Assigning an OPEN ticket also moves
 *  it to IN_REVIEW server-side (picking it up IS starting the review). */
export const assignGrievance = async (
  id: string,
  assignedEmptId: string | null,
  note?: string
) => {
  const res = await api.put(`/grievances/${id}/assign`, {
    assigned_empt_id: assignedEmptId,
    note: note || null,
  });
  return res.data as Grievance;
};

export const setGrievancePriority = async (
  id: string,
  priority: GrievancePriority,
  note?: string
) => {
  const res = await api.put(`/grievances/${id}/priority`, {
    priority,
    note: note || null,
  });
  return res.data as Grievance;
};

/* ---------------- SUBMITTER ACTIONS ---------------- */

export const withdrawGrievance = async (id: string, note?: string) => {
  const res = await api.put(`/grievances/${id}/withdraw`, { note: note || null });
  return res.data as Grievance;
};

/** Only a RESOLVED ticket reopens, and the reason is mandatory. */
export const reopenGrievance = async (id: string, note: string) => {
  const res = await api.put(`/grievances/${id}/reopen`, { note });
  return res.data as Grievance;
};

/* ---------------- CONVERSATION ---------------- */

export const postGrievanceMessage = async (
  id: string,
  body: string,
  options: { isInternal?: boolean; attachments?: GrievanceAttachment[] } = {}
) => {
  const res = await api.post(`/grievances/${id}/messages`, {
    body,
    is_internal: !!options.isInternal,
    attachments: options.attachments || [],
  });
  return res.data as Grievance;
};

/* ---------------- NOTIFICATION BELL FEED ---------------- */

export interface GrievanceNotification {
  id: string;
  kind: string; // SUBMITTED | ASSIGNED | MESSAGE | RESOLVED | CLOSED | REOPENED | …
  source: "grievance";
  grievance_id: string | null;
  task_id: string; // "grievance:<id>"
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
}

export const getGrievanceNotifications = (limit = 50) =>
  api.get<{ total: number; items: GrievanceNotification[] }>(
    `/grievances/notifications/unread?limit=${limit}`
  );

export const markGrievanceNotificationRead = (id: string) =>
  api.post(`/grievances/notifications/${id}/read`);

/* ---------------- DISPLAY HELPERS ---------------- */

export const TYPE_LABELS: Record<GrievanceType, string> = {
  FEEDBACK: "Feedback",
  COMPLAINT: "Complaint",
  SUGGESTION: "Suggestion",
  WORKPLACE_ISSUE: "Workplace Issue",
};

export const STATUS_LABELS: Record<GrievanceStatus, string> = {
  OPEN: "Open",
  IN_REVIEW: "In Review",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
  REOPENED: "Reopened",
  WITHDRAWN: "Withdrawn",
};

export const PRIORITY_LABELS: Record<GrievancePriority, string> = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  URGENT: "Urgent",
};

/** Map a status onto the shared `gt-pill` tints from global-theme.css, so these
 *  badges match every other status chip in the app. */
export const statusPillClass = (status: GrievanceStatus | string): string => {
  switch (status) {
    case "OPEN":
      return "gt-pill is-pending";
    case "IN_REVIEW":
      return "gt-pill is-progress";
    case "RESOLVED":
      return "gt-pill is-done";
    case "CLOSED":
      return "gt-pill is-ghost";
    case "REOPENED":
      return "gt-pill is-overdue";
    case "WITHDRAWN":
      return "gt-pill is-ghost";
    default:
      return "gt-pill is-pending";
  }
};

export const priorityPillClass = (priority: GrievancePriority | string): string => {
  switch (priority) {
    case "URGENT":
      return "gt-pill is-urgent";
    case "HIGH":
      return "gt-pill is-high";
    case "MEDIUM":
      return "gt-pill is-medium";
    case "LOW":
      return "gt-pill is-low";
    default:
      return "gt-pill is-medium";
  }
};

/** Human sentence for one audit-ledger row — the History timeline's line. */
export const describeEvent = (event: GrievanceEvent): string => {
  const to = event.to_value || "";
  const from = event.from_value || "";
  switch (event.action) {
    case "SUBMITTED":
      return "raised this submission";
    case "EDITED":
      return "edited the details";
    case "STATUS_CHANGED":
      return from && to ? `moved it from ${from} to ${to}` : `changed the status to ${to}`;
    case "ASSIGNED":
      return from ? `reassigned it from ${from} to ${to}` : `assigned it to ${to}`;
    case "UNASSIGNED":
      return from ? `removed ${from} as the handler` : "removed the handler";
    case "PRIORITY_CHANGED":
      return `changed the priority from ${from} to ${to}`;
    case "MESSAGE":
      return "replied";
    case "INTERNAL_NOTE":
      return "added an internal note";
    case "ATTACHMENT_ADDED":
      return "added an attachment";
    case "RESOLVED":
      return "resolved it";
    case "CLOSED":
      return "closed it";
    case "REOPENED":
      return "reopened it";
    case "WITHDRAWN":
      return "withdrew it";
    default:
      return event.action.toLowerCase().replace(/_/g, " ");
  }
};
